import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

import { SigninEventTokenService } from '../services/event-token.service';
import { SigninEventAttendeeService } from '../services/event-attendee.service';
import { SigninEventEvaluationService } from '../services/event-evaluation.service';
import { SigninCallPlanContactService } from '../services/call-plan-contact.service';
import { SigninListService } from '../services/list.service';
import { SignatureFieldComponent } from '../signature-field/signature-field.component';
import {
  getErrorMessage,
  isAttendeeEligibleForSignIn,
  mapAttendeeToCallPlan,
  mapCallPlanContactToCallPlan,
  normalizeToken,
  parseQuestionChoices,
} from '../utils/helpers';

type SigninView = 'people' | 'signin' | 'evaluation' | 'thankyou';

const ATTENDEE_SIGN_IN_TYPE = 'attendee_sign_in';

@Component({
  selector: 'app-attendee-list',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SignatureFieldComponent],
  templateUrl: './attendee-list.component.html',
  styleUrl: './attendee-list.component.css',
})
export class AttendeeListComponent implements OnInit, OnDestroy {
  @ViewChild('peopleSection') peopleSectionRef!: ElementRef<HTMLElement>;
  @ViewChild(SignatureFieldComponent) signatureField?: SignatureFieldComponent;

  searchForm = new FormGroup({
    first_name: new FormControl(''),
    last_name: new FormControl(''),
    city: new FormControl(''),
    state: new FormControl(''),
  });

  companyId: string | number = '';
  eventId: string | number = '';
  event: any = null;
  states: any[] = [];
  attendeeSpecialities: any[] = [];
  hcpTypes: any[] = [];
  attendees: any[] = [];
  attendeesLoaded = false;
  token = '';

  view: SigninView = 'people';
  callPlan: any = {};
  submitting = false;

  evalQuestions: any[] = [];
  evalAnswers: Record<number, any> = {};
  evalLoading = false;
  evalSubmitting = false;

  mapImageFailed = false;
  searchAllResults: any[] | null = null;
  searchAllInProgress = false;

  toastSuccess = '';
  toastError = '';

  // The state field is pre-filled with the venue state as a visual default, but we
  // don't want it to silently filter the attendee list on page load — the user
  // should see everyone first, then narrow down by interacting with the form.
  userHasSearched = false;

  private destroy$ = new Subject<void>();
  private toastTimer: any = null;

  private route = inject(ActivatedRoute);
  private eventTokenService = inject(SigninEventTokenService);
  private eventAttendeeService = inject(SigninEventAttendeeService);
  private eventEvaluationService = inject(SigninEventEvaluationService);
  private callPlanContactService = inject(SigninCallPlanContactService);
  private listService = inject(SigninListService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.searchForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.userHasSearched = true;
      if (this.searchAllResults) this.searchAllResults = null;
    });

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((queryParams) => {
      const tokenParam = queryParams['token'];
      if (!tokenParam) return;

      this.token = normalizeToken(tokenParam);
      const type = queryParams['type'] || ATTENDEE_SIGN_IN_TYPE;

      this.eventTokenService.decodeToken({ token: this.token, type }).subscribe({
        next: (data) => {
          this.companyId = data.companyId;
          this.eventId = data.eventId;
          this.event = data.event;
          const venueState = this.event?.venue?.state;
          if (venueState && !this.searchForm.value.state) {
            // emitEvent: false so the default state doesn't flip userHasSearched
            // and doesn't trigger a filtered view before the user interacts.
            this.searchForm.patchValue({ state: venueState }, { emitEvent: false });
          }
          this.cdr.detectChanges();

          this.loadAttendees();

          this.listService.getLists('CountryState,attendee_specialty').subscribe({
            next: (lists) => {
              this.states = lists.CountryState || [];
              this.attendeeSpecialities = lists.attendee_specialty || [];
              this.cdr.detectChanges();
            },
            error: () => {},
          });

          this.listService.getListsForCompany(this.companyId, 'hcp_type').subscribe({
            next: (lists) => {
              this.hcpTypes = lists.hcp_type || [];
              this.cdr.detectChanges();
            },
            error: () => {},
          });
        },
        error: (err) => {
          this.showError(getErrorMessage(err, 'Unable to decode sign-in token.'));
        },
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  loadAttendees() {
    this.eventAttendeeService
      .list({ company_id: this.companyId, event_id: this.eventId }, { token: this.token })
      .subscribe({
        next: (attendees) => {
          this.attendees = attendees || [];
          this.attendeesLoaded = true;
          this.cdr.detectChanges();
        },
        error: () => {
          this.attendees = [];
          this.attendeesLoaded = true;
          this.cdr.detectChanges();
        },
      });
  }

  get displayedResults(): any[] {
    if (this.searchAllResults) {
      return this.searchAllResults;
    }
    if (!this.attendees) return [];

    // Only apply a filter for a field the user has actually touched. The state
    // field is pre-filled with the venue state as a visual default via
    // patchValue({ emitEvent: false }) — `dirty` stays false until the user
    // changes it, so it won't silently filter out attendees while typing a name.
    const dirtyValue = (name: string): string => {
      const ctrl = this.searchForm.get(name);
      return ctrl?.dirty ? (ctrl.value || '') : '';
    };
    const first_name = dirtyValue('first_name');
    const last_name = dirtyValue('last_name');
    const city = dirtyValue('city');
    const state = dirtyValue('state');

    return this.attendees.filter((attendee) => {
      if (!isAttendeeEligibleForSignIn(attendee)) return false;
      if (last_name && !attendee.last_name?.toLowerCase().includes(last_name.toLowerCase())) return false;
      if (first_name && !attendee.first_name?.toLowerCase().includes(first_name.toLowerCase())) return false;
      if (city && !attendee.address?.city?.toLowerCase().includes(city.toLowerCase())) return false;
      if (state && attendee.address?.state !== state) return false;
      return true;
    });
  }

  get canSearchAll(): boolean {
    const { last_name, state } = this.searchForm.value;
    return !!(last_name && last_name.trim() && state);
  }

  getAttendeeDegree(attendee: any): string {
    if (attendee?.degrees && attendee.degrees.length) {
      return attendee.degrees.filter(Boolean).join(', ');
    }
    return attendee?.degree || '';
  }

  attendeeDisplayName(attendee: any): string {
    const name = [attendee?.first_name, attendee?.last_name].filter(Boolean).join(' ').trim();
    const degree = this.getAttendeeDegree(attendee);
    return degree ? `${name}, ${degree}` : name;
  }

  attendeeInitials(attendee: any): string {
    const f = (attendee?.first_name || '').charAt(0);
    const l = (attendee?.last_name || '').charAt(0);
    return `${f}${l}`.toUpperCase() || '?';
  }

  attendeeSublabel(attendee: any): string {
    const city = attendee?.address?.city ?? attendee?.city ?? '';
    const state = attendee?.address?.state ?? attendee?.state ?? '';
    return [city, state].filter(Boolean).join(', ');
  }

  speakerSublabel(speaker: any): string {
    return [speaker?.title, speaker?.affiliation].filter(Boolean).join(' · ');
  }

  /**
   * Only confirmed speakers should appear in the People list — matches the admin
   * Event Home view (which shows "Speaker: Wendy Brant, MD (Confirmed)" only) and
   * the registration site, which also filters speakers by status === 'confirmed'.
   * Speakers in 'awaiting_reply' / 'declined' / etc. are not yet committed and
   * shouldn't be presented to attendees as the program speaker.
   */
  get confirmedSpeakers(): any[] {
    const list = Array.isArray(this.event?.speakers) ? this.event.speakers : [];
    return list.filter((s: any) => (s?.status || '').toString().toLowerCase() === 'confirmed');
  }

  /**
   * Reps assigned to the event. The decoded token currently exposes a single rep via
   * `event.client`; if the backend ever returns a `reps` / `clients` array we surface
   * those too.
   */
  get reps(): any[] {
    const list: any[] = [];
    if (Array.isArray(this.event?.reps)) list.push(...this.event.reps);
    else if (Array.isArray(this.event?.clients)) list.push(...this.event.clients);
    else if (this.event?.client) list.push(this.event.client);
    return list.filter((r) => r && (r.first_name || r.last_name));
  }

  fullSpeakerName(speaker: any): string {
    const name = [speaker?.first_name, speaker?.last_name].filter(Boolean).join(' ').trim();
    const degree = (speaker?.degrees || []).filter(Boolean).join(', ') || speaker?.degree || '';
    return degree ? `${name}, ${degree}` : name;
  }

  get eventTitle(): string {
    if (!this.event) return '';
    const t = this.event.topic;
    if (t && typeof t === 'object') return t.display_name || t.name || this.event.name || '';
    return t || this.event.name || '';
  }

  get topicProducts(): any[] {
    const products = this.event?.topic?.products;
    return Array.isArray(products) ? products.filter((p) => p && p.image) : [];
  }

  get meetingTypeName(): string {
    const mt = this.event?.meeting_type;
    if (!mt) return '';
    return typeof mt === 'string' ? mt : (mt.name || mt.display_name || '');
  }

  get eventTimezoneId(): string {
    return this.event?.timezone_id || this.event?.timezone || '';
  }

  get eventFriendlyDate(): string {
    const startAt = this.event?.start_at;
    if (!startAt) return '';
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    if (this.eventTimezoneId) options.timeZone = this.eventTimezoneId;
    try {
      return new Date(startAt).toLocaleDateString('en-US', options);
    } catch {
      return new Date(startAt).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    }
  }

  get eventStartTime(): string {
    const startAt = this.event?.start_at;
    if (!startAt) return '';
    const options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
    if (this.eventTimezoneId) options.timeZone = this.eventTimezoneId;
    try {
      return new Date(startAt).toLocaleString('en-US', options);
    } catch {
      return new Date(startAt).toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    }
  }

  get eventTimezoneShort(): string {
    if (!this.eventTimezoneId) return '';
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: this.eventTimezoneId,
        timeZoneName: 'short',
      }).formatToParts(new Date());
      return parts.find((p) => p.type === 'timeZoneName')?.value || this.eventTimezoneId;
    } catch {
      return this.eventTimezoneId;
    }
  }

  get venueAddress(): string {
    const v = this.event?.venue;
    if (!v) return '';
    const parts = [v.address1, v.city, v.state].filter(Boolean);
    return v.zip ? `${parts.join(', ')} ${v.zip}` : parts.join(', ');
  }

  get staticMapUrl(): string | null {
    const v = this.event?.venue;
    if (!v?.latitude || !v?.longitude) return null;
    const zoom = 15;
    const x = Math.floor((v.longitude + 180) / 360 * Math.pow(2, zoom));
    const rad = v.latitude * Math.PI / 180;
    const y = Math.floor((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2 * Math.pow(2, zoom));
    return `https://a.basemaps.cartocdn.com/rastertiles/voyager/${zoom}/${x}/${y}@2x.png`;
  }

  private getMapPinFraction(): { left: number; top: number } | null {
    const v = this.event?.venue;
    if (!v?.latitude || !v?.longitude) return null;
    const zoom = 15;
    const xFrac = (v.longitude + 180) / 360 * Math.pow(2, zoom);
    const rad = v.latitude * Math.PI / 180;
    const yFrac = (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2 * Math.pow(2, zoom);
    return { left: xFrac - Math.floor(xFrac), top: yFrac - Math.floor(yFrac) };
  }

  get mapPinLeft(): string {
    const pos = this.getMapPinFraction();
    return pos ? `${pos.left * 100}%` : '50%';
  }

  get mapImageTransform(): string {
    const pos = this.getMapPinFraction();
    if (!pos) return '';
    return `translateY(calc(100px - ${pos.top * 100}%))`;
  }

  get googleMapsUrl(): string | null {
    const v = this.event?.venue;
    if (!v) return null;
    if (v.google_maps_uri) return v.google_maps_uri;
    if (v.latitude && v.longitude) return `https://www.google.com/maps?q=${v.latitude},${v.longitude}`;
    if (v.address1 && v.city && v.state) {
      const q = `${v.address1}, ${v.city}, ${v.state} ${v.zip || ''}`.trim();
      return `https://www.google.com/maps?q=${encodeURIComponent(q)}`;
    }
    return null;
  }

  get usStates(): any[] {
    return (this.states || []).filter((s) => s?.country_id === 'US');
  }

  /**
   * When the event's sub-status is `closed` or `reconciled` the program is
   * already wrapped up — no more sign-ins should be accepted. The greeting
   * card swaps to a "thank you" message and the Attendee Search block (form,
   * results, action buttons) is hidden entirely.
   */
  get isEventConcluded(): boolean {
    const subStatusId = (this.event?.sub_status?.id || '').toString().toLowerCase();
    return subStatusId === 'closed' || subStatusId === 'reconciled';
  }

  scrollToPeople(): void {
    this.peopleSectionRef?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  selectSearchResult(result: any): void {
    if (result?._source === 'call_plan_contact') {
      this.callPlan = mapCallPlanContactToCallPlan(result);
    } else {
      this.callPlan = mapAttendeeToCallPlan(result);
    }
    this.view = 'signin';
    setTimeout(() => {
      this.peopleSectionRef?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  /**
   * Open the sign-in form with a blank profile prefilled from the current
   * search inputs — used when an attendee isn't in the existing audience or
   * call-plan contacts and needs to be added on the fly. Mirrors the walk-in
   * defaults that `mapCallPlanContactToCallPlan` applies (no `id`, walked_in,
   * attended_status_id='attended').
   */
  createNewProfile(): void {
    const { first_name, last_name, city, state } = this.searchForm.value;
    this.callPlan = {
      first_name: (first_name || '').trim(),
      middle_name: '',
      last_name: (last_name || '').trim(),
      affiliation: '',
      address1: '',
      address2: '',
      city: (city || '').trim(),
      state: state || '',
      zip: '',
      hcp_type: '',
      specialty: '',
      vendor_code: '',
      npi_id: '',
      state_license_number: '',
      license_state: '',
      cell_phone: '',
      email: '',
      degree: '',
      meal_opt_out: false,
      walked_in: true,
      attended_status_id: 'attended',
      signature: '',
    };
    this.view = 'signin';
    setTimeout(() => {
      this.peopleSectionRef?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  /**
   * Whether the Create New Profile button should appear. Surfaces ONLY after
   * the user has run Search All Contacts — adding a brand-new profile is a
   * walk-in flow, so we want to make sure the user has first checked whether
   * the contact already exists in the call-plan system.
   */
  get canCreateNewProfile(): boolean {
    return this.searchAllResults !== null;
  }

  searchAllContacts(): void {
    if (!this.canSearchAll || this.searchAllInProgress) return;
    const { first_name, last_name, state } = this.searchForm.value;
    this.searchAllInProgress = true;

    // Attendees match by name only (ignore city/state per requirement).
    const trimmedFirst = (first_name || '').trim().toLowerCase();
    const trimmedLast = (last_name || '').trim().toLowerCase();
    const attendeeMatches = (this.attendees || []).filter((a) => {
      if (trimmedLast && !a.last_name?.toLowerCase().includes(trimmedLast)) return false;
      if (trimmedFirst && !a.first_name?.toLowerCase().includes(trimmedFirst)) return false;
      return true;
    });
    const attendeeContactIds = new Set(
      attendeeMatches.map((a) => a?.contact_id).filter((v) => v !== undefined && v !== null),
    );

    this.callPlanContactService
      .list(this.companyId, {
        token: this.token,
        last_name,
        first_name: first_name || undefined,
        state,
        attended: true,
        limit: 50,
      })
      .subscribe({
        next: (resp) => {
          const cpc = Array.isArray(resp?.call_plan_contacts) ? resp.call_plan_contacts : [];
          const deduped = cpc.filter((c) => {
            const cid = c?.type === 'contact' ? c?.id : c?.contact_id;
            if (cid !== undefined && cid !== null && attendeeContactIds.has(cid)) return false;
            return true;
          });
          const merged = [
            ...attendeeMatches.map((a) => ({ ...a, _source: 'attendee' })),
            ...deduped.map((c) => ({ ...c, _source: 'call_plan_contact' })),
          ];
          this.searchAllResults = merged;
          this.searchAllInProgress = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.searchAllInProgress = false;
          this.showError(getErrorMessage(err, 'Search failed. Please try again.'));
          this.cdr.detectChanges();
        },
      });
  }

  resetSearchAll(): void {
    this.searchAllResults = null;
  }

  backToPeople(): void {
    this.view = 'people';
    this.callPlan = {};
    this.evalQuestions = [];
    this.evalAnswers = {};
  }

  onSubmit(form: NgForm): void {
    if (!form.valid) return;
    this.submitting = true;
    const urlParams = { company_id: this.companyId, event_id: this.eventId };
    const apiParams = { token: this.token };
    const request$ = this.callPlan.id
      ? this.eventAttendeeService.update(this.callPlan.id, this.callPlan, urlParams, apiParams)
      : this.eventAttendeeService.create(this.callPlan, urlParams, apiParams);
    request$.subscribe({
      next: () => {
        this.submitting = false;
        this.showSuccess('Successfully signed in!');
        if (this.event?.attendee_evaluation_id) {
          this.loadEvaluation();
        } else {
          this.goToThankYou();
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.submitting = false;
        this.showError(getErrorMessage(err, 'Sign-in failed. Please try again.'));
        this.cdr.detectChanges();
      },
    });
  }

  clearSignature(): void {
    this.signatureField?.clear();
    this.callPlan.signature = '';
  }

  loadEvaluation(): void {
    this.evalLoading = true;
    this.view = 'evaluation';
    this.evalQuestions = [];
    this.evalAnswers = {};
    this.eventEvaluationService
      .getAttendeeQuestions(
        { company_id: this.companyId, event_id: this.eventId },
        { token: this.token }
      )
      .subscribe({
        next: (questions) => {
          let displayNumber = 0;
          this.evalQuestions = (Array.isArray(questions) ? questions : []).map((eq) => {
            const type = eq?.question?.type_id;
            if (type && type !== 'HEADER' && type !== 'PARAGRAPH') {
              displayNumber += 1;
              return { ...eq, _displayNumber: displayNumber };
            }
            return { ...eq, _displayNumber: null };
          });
          if (!this.evalQuestions.length) {
            this.goToThankYou();
          }
          this.evalLoading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.evalLoading = false;
          this.showError(getErrorMessage(err, 'Unable to load the evaluation. Please try again.'));
          this.goToThankYou();
          this.cdr.detectChanges();
        },
      });
  }

  onSubmitEvaluation(form: NgForm): void {
    if (!form.valid) return;
    this.evalSubmitting = true;
    const answers: Record<number, any> = {};
    for (const eq of this.evalQuestions) {
      const id = eq?.question?.id;
      if (!id) continue;
      const value = this.evalAnswers[id];
      if (value === undefined || value === null || value === '') continue;
      if (Array.isArray(value) && value.length === 0) continue;
      answers[id] = value;
    }
    this.eventEvaluationService
      .submitAnonymousAnswers(
        { token: this.token, answers },
        { company_id: this.companyId, event_id: this.eventId }
      )
      .subscribe({
        next: () => {
          this.evalSubmitting = false;
          this.goToThankYou();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.evalSubmitting = false;
          this.showError(getErrorMessage(err, 'Submitting the evaluation failed. Please try again.'));
          this.cdr.detectChanges();
        },
      });
  }

  goToThankYou(): void {
    this.view = 'thankyou';
  }

  finishThankYou(): void {
    this.backToPeople();
    this.loadAttendees();
  }

  getQuestionTypeId(question: any): string {
    return question?.type_id || '';
  }

  getQuestionChoices(question: any): string[] {
    return parseQuestionChoices(question?.choices);
  }

  isChoiceSelected(questionId: number, choice: string): boolean {
    const value = this.evalAnswers[questionId];
    return Array.isArray(value) && value.includes(choice);
  }

  onMultiCheckChange(questionId: number, choice: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const current = Array.isArray(this.evalAnswers[questionId])
      ? [...this.evalAnswers[questionId]]
      : [];
    const index = current.indexOf(choice);
    if (checked && index === -1) {
      current.push(choice);
    } else if (!checked && index !== -1) {
      current.splice(index, 1);
    }
    this.evalAnswers[questionId] = current;
  }

  ratingScale(): number[] {
    return [1, 2, 3, 4, 5];
  }

  setRating(questionId: number, value: number): void {
    this.evalAnswers[questionId] = this.evalAnswers[questionId] === value ? null : value;
  }

  getRating(questionId: number): number {
    const value = this.evalAnswers[questionId];
    return typeof value === 'number' ? value : 0;
  }

  private showSuccess(msg: string): void {
    this.toastSuccess = msg;
    this.toastError = '';
    this.resetToastTimer();
  }

  private showError(msg: string): void {
    this.toastError = msg;
    this.toastSuccess = '';
    this.resetToastTimer();
  }

  dismissToast(): void {
    this.toastSuccess = '';
    this.toastError = '';
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  private resetToastTimer(): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      this.dismissToast();
      this.cdr.detectChanges();
    }, 5000);
  }
}
